import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "../components/branding/Logo";
import {
    ArrowLeft,
    Check,
    Copy,
    ExternalLink,
    Info,
    Layers,
    Link2,
    MessageCircle,
} from "lucide-react";

/**
 * Public embed docs page (`/embed`).
 *
 * Snippet URLs are pinned to `REACT_APP_BACKEND_URL` — the primary ingress
 * domain that routes `/api/*` to the backend — NOT `window.location.origin`.
 * This is critical because the app may be served from a static-CDN mirror
 * (e.g. `*.preview.static.emergentagent.com`) that has no backend routes,
 * so `origin`-based URLs would 403.
 */
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function Embed() {
    const origin = BACKEND_URL || "https://your-meowmd-tools-domain";

    const snippetLink = useMemo(
        () =>
            `<a
  href="${origin}/health-check"
  target="_blank" rel="noopener noreferrer"
  style="display:inline-flex;align-items:center;gap:8px;
    background:#2A8B8B;color:#fff;padding:14px 22px;
    border-radius:9999px;font:600 15px/1 'DM Sans',sans-serif;
    text-decoration:none;box-shadow:0 10px 24px rgba(42,139,139,0.28);"
>Try the Free Cat Health Check →</a>`,
        [origin],
    );

    const snippetIframe = useMemo(
        () =>
            `<iframe
  src="${origin}/health-check"
  title="MeowMD Free Cat Health Check"
  loading="lazy"
  allow="camera; clipboard-read; clipboard-write"
  style="width:100%;max-width:480px;min-height:820px;height:100dvh;
    border:0;border-radius:22px;box-shadow:0 12px 40px rgba(42,42,42,0.08);"
></iframe>`,
        [origin],
    );

    const snippetWidget = useMemo(
        () => `<script src="${origin}/api/widget.js" async></script>`,
        [origin],
    );

    return (
        <div className="meow-shell" data-testid="embed-page" style={{ maxWidth: 720 }}>
            <header className="sticky top-0 z-20 flex items-center justify-between bg-meow-cream/95 px-5 py-4 backdrop-blur">
                <Link
                    to="/"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(42,42,42,0.06)] hover:bg-white/90"
                    aria-label="Back"
                    data-testid="embed-back-btn"
                >
                    <ArrowLeft className="h-4 w-4 text-meow-charcoal" strokeWidth={2} />
                </Link>
                <Logo />
                <span className="w-9" />
            </header>

            <main className="px-5 pb-24 pt-4">
                <span className="eyebrow" data-testid="embed-eyebrow">
                    For meowmd.in webmasters
                </span>
                <h1
                    className="mt-2 font-display text-[2.15rem] font-medium leading-tight text-meow-charcoal"
                    data-testid="embed-heading"
                >
                    Embed the tools on any page.
                </h1>
                <p className="mt-2 text-[14.5px] leading-relaxed text-meow-muted">
                    Paste the snippets below into your site. They point at{" "}
                    <code
                        className="rounded bg-white px-1.5 py-0.5 font-mono text-[12.5px] text-meow-charcoal break-all"
                        data-testid="embed-current-origin"
                    >
                        {origin}
                    </code>{" "}
                    — the primary backend domain. When this app is redeployed,
                    rebuild once and every snippet on this page updates.
                </p>

                <Tool
                    icon={<Link2 className="h-4 w-4" />}
                    eyebrow="Tool 1 · Free Health Check"
                    title="Option A — Direct link (recommended)"
                    body="Simplest and most visible. Opens the full-screen tool in a new tab, matches the flow's mobile-first design 1:1, and adds no iframe overhead. Recommended for hero CTAs, blog posts, and email footers."
                    snippet={snippetLink}
                    snippetTestId="snippet-link"
                    preview={
                        <a
                            href={`${origin}/health-check`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="meow-btn-primary text-[15px]"
                            data-testid="embed-link-preview"
                        >
                            Try the Free Cat Health Check
                            <ExternalLink className="ml-2 h-4 w-4" strokeWidth={2} />
                        </a>
                    }
                />

                <Tool
                    icon={<Layers className="h-4 w-4" />}
                    eyebrow="Tool 1 · Free Health Check"
                    title="Option B — Iframe embed"
                    body="Use when you want the tool to live inline on your page (e.g. a dedicated /free-health-check page on meowmd.in). Width is 100% up to 480px, min-height is 820px — enough for every step. On desktop it sits as a neat phone-shaped column; on mobile it uses the full width and height. Section-level embed works best; avoid nesting inside narrow sidebars."
                    snippet={snippetIframe}
                    snippetTestId="snippet-iframe"
                />

                <Tool
                    icon={<MessageCircle className="h-4 w-4" />}
                    eyebrow="Tool 2 · Support Chat Widget"
                    title="One-line embed — bottom-right chat bubble"
                    body="Paste this once, just before the closing </body> tag on every page you want the widget to appear on (or in your site-wide template). The widget is Shadow-DOM isolated so it can't collide with your CSS. Answers pricing and feature questions from a fixed knowledge base, shows real app screenshots when relevant, and hands off to support@meowai.health for anything else."
                    snippet={snippetWidget}
                    snippetTestId="snippet-widget"
                />

                <div
                    className="mt-8 flex items-start gap-3 rounded-2xl bg-white p-4 shadow-[0_4px_20px_rgba(42,42,42,0.04)]"
                    data-testid="embed-note"
                >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-meow-icon text-meow-teal">
                        <Info className="h-4 w-4" strokeWidth={2} />
                    </div>
                    <div className="text-[13.5px] leading-relaxed text-meow-charcoal/90">
                        The URLs are pinned to the primary backend domain, so
                        the snippets work even if this page is mirrored to a
                        static CDN host.
                    </div>
                </div>

                <div className="mt-6 flex flex-col gap-2 text-[13px] text-meow-muted">
                    <Link
                        to="/embed-test"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-meow-teal hover:underline"
                        data-testid="link-embed-test"
                    >
                        See the widget on a deliberately-different foreign page
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                    </Link>
                    <Link
                        to="/iframe-test"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-meow-teal hover:underline"
                        data-testid="link-iframe-test"
                    >
                        See the Health Check running inside a foreign iframe
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                    </Link>
                </div>
            </main>
        </div>
    );
}

function Tool({ icon, eyebrow, title, body, snippet, snippetTestId, preview }) {
    return (
        <section
            className="mt-8 rounded-2xl bg-white p-5 shadow-[0_6px_24px_rgba(42,42,42,0.04)]"
            data-testid={`tool-${snippetTestId}`}
        >
            <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-meow-icon text-meow-teal">
                    {icon}
                </div>
                <span className="eyebrow text-meow-muted">{eyebrow}</span>
            </div>
            <h2 className="mt-3 font-display text-[1.25rem] font-medium text-meow-charcoal">
                {title}
            </h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-meow-muted">{body}</p>

            {preview && (
                <div
                    className="mt-4 flex items-center justify-center rounded-xl bg-meow-cream/70 py-6"
                    data-testid={`${snippetTestId}-preview`}
                >
                    {preview}
                </div>
            )}

            <SnippetBox testId={snippetTestId} snippet={snippet} />
        </section>
    );
}

/**
 * Snippet with a Copy button in a HEADER BAR above the code (never overlaid).
 * On success shows a sonner toast + swaps the button to a bright gold
 * "Copied!" state for ~2s so the confirmation is unmissable.
 */
function SnippetBox({ testId, snippet }) {
    const [copied, setCopied] = useState(false);

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
        } catch {
            // Fallback for older browsers / http contexts
            const ta = document.createElement("textarea");
            ta.value = snippet;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
            } catch {
                /* give up silently */
            }
            document.body.removeChild(ta);
        }
        setCopied(true);
        toast.success("Snippet copied to clipboard", { duration: 2200 });
        setTimeout(() => setCopied(false), 2200);
    };

    return (
        <div className="mt-4 overflow-hidden rounded-xl bg-meow-charcoal">
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
                <span className="text-[11px] font-medium uppercase tracking-widest text-white/60">
                    Snippet
                </span>
                <button
                    type="button"
                    onClick={doCopy}
                    className={
                        "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors " +
                        (copied
                            ? "bg-meow-gold text-meow-charcoal"
                            : "bg-white/12 text-white hover:bg-white/22")
                    }
                    style={
                        copied
                            ? { backgroundColor: "#D4A017", color: "#2A2A2A" }
                            : { backgroundColor: "rgba(255,255,255,0.14)" }
                    }
                    data-testid={`${testId}-copy`}
                    aria-live="polite"
                    aria-label={copied ? "Copied" : "Copy snippet"}
                >
                    {copied ? (
                        <>
                            <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            Copied!
                        </>
                    ) : (
                        <>
                            <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                            Copy
                        </>
                    )}
                </button>
            </div>
            <pre
                className="max-h-64 overflow-x-auto overflow-y-auto p-4 font-mono text-[12.5px] leading-relaxed text-meow-cream"
                data-testid={testId}
            >
                <code>{snippet}</code>
            </pre>
        </div>
    );
}
